import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@alphabait/ui'
import { MultiSelect } from '@/components/multi-select'
import {
  EndUsersUploadedError,
  Form,
} from '@/types/common'
import { useEndUsersIntegrationsClient } from '@/lib/api'
import Loading from '../../../../components/loading'
import { GetCampaignFragment } from '@/types/campaign'

export default function OneCommunityForm({
  campaign,
  onComplete,
  setErrors,
  setShowModal,
}: {
  campaign: GetCampaignFragment
  onComplete: () => void
  setErrors: (errors?: EndUsersUploadedError[]) => void
  setShowModal: (show: boolean) => void
}) {
  const isOneCommunityFormsCampaign = useMemo(
    () =>
      campaign?.campaignCategory === 'lottery' &&
      campaign?.customer?.integrations?.some(
        (i: any) => i?.type === 'ONE_COMMUNITY' && i.features?.forms?.enabled,
      ),
    [campaign?.campaignCategory, campaign?.customer?.integrations],
  )
  const { t } = useTranslation()
  const integrationsClient = useEndUsersIntegrationsClient()

  const { data: formsResponse, isLoading: areFormsLoading } = useQuery({
    queryKey: ['oneCommunityForms', campaign.id],
    queryFn: async () =>
      await integrationsClient.getForms.query({ params: { campaignId: campaign.id } }),
    enabled: isOneCommunityFormsCampaign,
  })

  const [selectedForms, setSelectedForms] = useState<string[]>(
    campaign.optionalSettings?.oneCommunity?.formIds ?? [],
  )

  const fetchFormEntitiesMutation = useMutation({
    mutationFn: async (data: { campaignId: string; formIds: string }) => {
      const response = await integrationsClient.getFormEntities.query({
        params: { campaignId: data.campaignId },
        query: { formIds: data.formIds },
      })

      // Handle validation errors (400 responses)
      if (response.status === 400) {
        throw response.body
      }

      return response
    },
    onSuccess: () => {
      onComplete()
    },
    onError: (error: any) => {
      // Backend returns array of errors directly
      if (Array.isArray(error)) {
        setErrors(error)
        setShowModal(true)
        return
      }

      console.error('Error fetching form entities:', error)
    },
  })

  if (!isOneCommunityFormsCampaign) {
    return <></>
  }
  if (areFormsLoading || fetchFormEntitiesMutation.isPending) return <Loading />

  const forms = (formsResponse && Array.isArray(formsResponse.body)) ? formsResponse.body : []

  return (
    <div className="flex flex-row justify-between bg-ui-100 rounded-lg py-4 px-2 items-center">
      <div className="text-gray-200 text-sm">
        <>
          <h1 className="px-1 text-base font-bold">
            {t('api_integration.lottery_header')}
          </h1>
          <p className="p-1 text-sm font-normal">
            {t('api_integration.lottery_text')}
          </p>
        </>

        <div className="w-1/5 grid">
          <MultiSelect
            options={forms.map((form: Form) => ({
              value: form.id?.toString() ?? '',
              label: form.label ?? '',
              data: { id: form.id, label: form.label },
            }))}
            selected={selectedForms}
            onChange={setSelectedForms}
            className="bg-white w-80"
          />
        </div>
      </div>
      <Button
        onClick={() => {
          fetchFormEntitiesMutation.mutate({
            campaignId: campaign.id,
            formIds: selectedForms.join(','),
          })
        }}
        className="flex flex-row cursor-pointer text-white"
        disabled={fetchFormEntitiesMutation.isPending || !selectedForms?.length}
      >
        {fetchFormEntitiesMutation.isPending ? 'Loading...' : t('api_integration.sync')}
      </Button>
    </div>
  )
}
